import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import {
  Clock, User, Save, RefreshCw, Settings, Calendar, X, Check, Edit2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' }
];

const DEFAULT_DAY = {
  enabled: true,
  start_hour: 7,
  start_minute: 30,
  end_hour: 17,
  end_minute: 0
};

const DEFAULT_WEEKEND = {
  enabled: false,
  start_hour: 0,
  start_minute: 0,
  end_hour: 0,
  end_minute: 0
};

// Time picker options
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

export default function AgentWorkHoursConfig() {
  const [agents, setAgents] = useState([]);
  const [workHoursConfig, setWorkHoursConfig] = useState({});
  const [clockifyUsers, setClockifyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit dialog
  const [editAgent, setEditAgent] = useState(null);
  const [editConfig, setEditConfig] = useState(null);

  // Load agents and work hours
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load agents
      const agentsRes = await axios.get(`${API}/api/agents/mapping`);
      setAgents(agentsRes.data.agents || []);
      
      // Load existing work hours
      const workHoursRes = await axios.get(`${API}/api/agents/work-hours`);
      const workHoursMap = {};
      (workHoursRes.data.work_hours || []).forEach(wh => {
        workHoursMap[wh.agent_id] = wh;
      });
      setWorkHoursConfig(workHoursMap);
      
      // Try to load Clockify users for mapping
      try {
        const clockifyRes = await axios.get(`${API}/api/clockify/users-mapping`);
        setClockifyUsers(clockifyRes.data.clockify_users || []);
      } catch (e) {
        // Clockify may not be configured
        console.log("Clockify not available for mapping");
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load agent data");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Open edit dialog for an agent
  const openEditDialog = async (agent) => {
    try {
      // Fetch current config (or defaults)
      const res = await axios.get(`${API}/api/agents/work-hours/${agent.id}`);
      setEditAgent(agent);
      setEditConfig(res.data);
    } catch (error) {
      toast.error("Failed to load work hours");
    }
  };

  // Update a day's config in edit state
  const updateDayConfig = (dayKey, field, value) => {
    setEditConfig(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: value
      }
    }));
  };

  // Copy Monday's hours to all weekdays
  const copyMondayToWeekdays = () => {
    const mondayConfig = editConfig.monday;
    setEditConfig(prev => ({
      ...prev,
      tuesday: { ...mondayConfig },
      wednesday: { ...mondayConfig },
      thursday: { ...mondayConfig },
      friday: { ...mondayConfig }
    }));
    toast.success("Copied Monday's hours to all weekdays");
  };

  // Save work hours
  const saveWorkHours = async () => {
    if (!editAgent || !editConfig) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/api/agents/work-hours/${editAgent.id}`, {
        ...editConfig,
        agent_id: editAgent.id,
        agent_name: editAgent.name
      });
      
      // Update local state
      setWorkHoursConfig(prev => ({
        ...prev,
        [editAgent.id]: editConfig
      }));
      
      toast.success(`Work hours saved for ${editAgent.name}`);
      setEditAgent(null);
      setEditConfig(null);
    } catch (error) {
      console.error("Failed to save work hours:", error);
      toast.error("Failed to save work hours");
    }
    setSaving(false);
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    if (!editAgent) return;
    
    try {
      await axios.delete(`${API}/api/agents/work-hours/${editAgent.id}`);
      
      // Remove from local state
      setWorkHoursConfig(prev => {
        const newConfig = { ...prev };
        delete newConfig[editAgent.id];
        return newConfig;
      });
      
      toast.success(`Reset to defaults for ${editAgent.name}`);
      setEditAgent(null);
      setEditConfig(null);
    } catch (error) {
      toast.error("Failed to reset work hours");
    }
  };

  // Format time for display
  const formatTime = (hour, minute) => {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // Get summary of work hours for an agent
  const getWorkHoursSummary = (agentId) => {
    const config = workHoursConfig[agentId];
    if (!config) return "Default (7:30 - 17:00, Mon-Fri)";
    
    // Check if all weekdays are the same
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const firstDay = config.monday;
    
    if (!firstDay) return "Custom schedule";
    
    const allSame = weekdays.every(d => {
      const dayConfig = config[d];
      return dayConfig &&
        dayConfig.enabled === firstDay.enabled &&
        dayConfig.start_hour === firstDay.start_hour &&
        dayConfig.start_minute === firstDay.start_minute &&
        dayConfig.end_hour === firstDay.end_hour &&
        dayConfig.end_minute === firstDay.end_minute;
    });
    
    if (allSame && firstDay.enabled) {
      const start = formatTime(firstDay.start_hour, firstDay.start_minute);
      const end = formatTime(firstDay.end_hour, firstDay.end_minute);
      
      // Check if it matches default
      if (start === "07:30" && end === "17:00") {
        return `${start} - ${end}, Mon-Fri`;
      }
      return `${start} - ${end}, Mon-Fri`;
    }
    
    return "Custom schedule";
  };
  
  // Check if agent has custom config
  const hasCustomConfig = (agentId) => {
    return !!workHoursConfig[agentId];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
        <span className="ml-2 text-gray-400">Loading agents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="agent-work-hours-config">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
            <Clock className="w-5 h-5 text-purple-500" />
            Agent Work Hours
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            Configure work hours per agent for after-hours analysis
          </p>
        </div>
        <Button
          onClick={loadData}
          variant="outline"
          size="sm"
          className="border-[#DAD5CA]"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DAD5CA]">
                <TableHead className="text-gray-400">Agent</TableHead>
                <TableHead className="text-gray-400">Work Hours</TableHead>
                <TableHead className="text-gray-400">Clockify Link</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.filter(a => a.active).map(agent => (
                <TableRow key={agent.id} className="border-[#DAD5CA]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-[#1A1A1A] text-sm font-medium">
                        {agent.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-[#1A1A1A] font-medium">{agent.name}</p>
                        <p className="text-xs text-gray-400">{agent.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-300 text-sm">
                      {getWorkHoursSummary(agent.id)}
                    </span>
                    {hasCustomConfig(agent.id) && (
                      <Badge className="ml-2 bg-purple-600/20 text-purple-400 border-0 text-xs">
                        Custom
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {workHoursConfig[agent.id]?.clockify_user_id ? (
                      <Badge className="bg-green-600/20 text-green-400 border-0">
                        <Check className="w-3 h-3 mr-1" />
                        Linked
                      </Badge>
                    ) : (
                      <span className="text-gray-500 text-sm">Not linked</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      onClick={() => openEditDialog(agent)}
                      variant="outline"
                      size="sm"
                      className="border-[#DAD5CA]"
                      data-testid={`edit-hours-${agent.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editAgent} onOpenChange={() => { setEditAgent(null); setEditConfig(null); }}>
        <DialogContent className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Work Hours: {editAgent?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editConfig && (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-6 py-4">
                {/* Timezone */}
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select 
                    value={String(editConfig.timezone_offset || 10)}
                    onValueChange={(v) => setEditConfig(prev => ({ ...prev, timezone_offset: parseInt(v) }))}
                  >
                    <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <SelectItem value="10">Brisbane (UTC+10)</SelectItem>
                      <SelectItem value="11">Sydney/Melbourne DST (UTC+11)</SelectItem>
                      <SelectItem value="9.5">Adelaide (UTC+9:30)</SelectItem>
                      <SelectItem value="8">Perth (UTC+8)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Clockify User Link */}
                {clockifyUsers.length > 0 && (
                  <div className="space-y-2">
                    <Label>Link to Clockify User</Label>
                    <Select 
                      value={editConfig.clockify_user_id || "none"}
                      onValueChange={(v) => setEditConfig(prev => ({ 
                        ...prev, 
                        clockify_user_id: v === "none" ? null : v 
                      }))}
                    >
                      <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA]">
                        <SelectValue placeholder="Select Clockify user" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                        <SelectItem value="none">Not linked</SelectItem>
                        {clockifyUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="flex gap-2">
                  <Button
                    onClick={copyMondayToWeekdays}
                    variant="outline"
                    size="sm"
                    className="border-[#DAD5CA]"
                  >
                    <Calendar className="w-4 h-4 mr-1" />
                    Copy Monday to Weekdays
                  </Button>
                </div>

                {/* Daily Schedule */}
                <div className="space-y-3">
                  <Label className="text-lg">Daily Schedule</Label>
                  
                  {DAYS.map(day => {
                    const dayConfig = editConfig[day.key] || (day.key === 'saturday' || day.key === 'sunday' ? DEFAULT_WEEKEND : DEFAULT_DAY);
                    
                    return (
                      <div 
                        key={day.key} 
                        className={`p-3 rounded-lg border ${dayConfig.enabled ? 'bg-[#FFFFFF] border-[#DAD5CA]' : 'bg-[#FFFFFF] border-[#F4F2ED]'}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={dayConfig.enabled}
                              onCheckedChange={(checked) => updateDayConfig(day.key, 'enabled', checked)}
                              data-testid={`${day.key}-enabled`}
                            />
                            <span className={`font-medium ${dayConfig.enabled ? 'text-[#1A1A1A]' : 'text-gray-500'}`}>
                              {day.label}
                            </span>
                          </div>
                          {dayConfig.enabled && (
                            <span className="text-sm text-gray-400">
                              {formatTime(dayConfig.start_hour, dayConfig.start_minute)} - {formatTime(dayConfig.end_hour, dayConfig.end_minute)}
                            </span>
                          )}
                        </div>
                        
                        {dayConfig.enabled && (
                          <div className="grid grid-cols-2 gap-4 mt-3">
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">Start Time</Label>
                              <div className="flex gap-2">
                                <Select
                                  value={String(dayConfig.start_hour)}
                                  onValueChange={(v) => updateDayConfig(day.key, 'start_hour', parseInt(v))}
                                >
                                  <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA] w-20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    {HOURS.map(h => (
                                      <SelectItem key={h} value={String(h)}>
                                        {h.toString().padStart(2, '0')}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <span className="text-gray-500 self-center">:</span>
                                <Select
                                  value={String(dayConfig.start_minute)}
                                  onValueChange={(v) => updateDayConfig(day.key, 'start_minute', parseInt(v))}
                                >
                                  <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA] w-20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    {MINUTES.map(m => (
                                      <SelectItem key={m} value={String(m)}>
                                        {m.toString().padStart(2, '0')}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-gray-400">End Time</Label>
                              <div className="flex gap-2">
                                <Select
                                  value={String(dayConfig.end_hour)}
                                  onValueChange={(v) => updateDayConfig(day.key, 'end_hour', parseInt(v))}
                                >
                                  <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA] w-20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    {HOURS.map(h => (
                                      <SelectItem key={h} value={String(h)}>
                                        {h.toString().padStart(2, '0')}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <span className="text-gray-500 self-center">:</span>
                                <Select
                                  value={String(dayConfig.end_minute)}
                                  onValueChange={(v) => updateDayConfig(day.key, 'end_minute', parseInt(v))}
                                >
                                  <SelectTrigger className="bg-[#FFFFFF] border-[#DAD5CA] w-20">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                                    {MINUTES.map(m => (
                                      <SelectItem key={m} value={String(m)}>
                                        {m.toString().padStart(2, '0')}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button
              onClick={resetToDefaults}
              variant="outline"
              className="border-red-800 text-red-400 hover:bg-red-900/20"
            >
              Reset to Defaults
            </Button>
            <Button
              onClick={() => { setEditAgent(null); setEditConfig(null); }}
              variant="outline"
              className="border-[#DAD5CA]"
            >
              Cancel
            </Button>
            <Button
              onClick={saveWorkHours}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-work-hours-btn"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
